import skipIcon from '../../../assets/svgs/skiped.svg';
import ReStartIcon from '../../../assets/svgs/restart-movex.svg';
import ExitIcon from '../../../assets/svgs/exit-movex.svg';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import TextWithIcon from './text-with-icon';
import ConfirmActionModal from './confirm-action-modal';
import ExerciseDetailsModal from './exercise-details-modal';
import ExerciseCard from '../../exercise-card';
import { useEffect, useRef, useState } from 'react';
import { HiOutlineClipboardDocumentList } from 'react-icons/hi2';
import { IoArrowForward } from 'react-icons/io5';
import { SettingMode, IN_PERFORMANCE_ACTION_TYPES } from '../../../constants/constants';
import { EXERCISE_STATUS } from '../../../utilis/exercise-status';
import { leaveSession, normalizeRedirectUrl } from '../../../utilis/session-redirect';
import { trackEvent } from '../../../services/analytics';
import { getInPerformanceTrackingProps } from '../../../zustand/session-metadata-store';
import tw from 'twin.macro';

const SettingModal = ({
  openModal,
  handleClose,
  handleStopSend,
  handleToggle,
  checked,
  cleanupStream,
  handleExit,
  sendJsonMessage,
  isPaseStage,
  currentExerciseOrder,
  setToggleMirror,
  toggleMirror,
  sessionId,
  onRestartExercise,
  onExerciseSkipped,
  mediaPipeEnabled,
  handleToggleMediaPipe,
  isMotionCapture,
  handleToggleExecutionMode,
  exitUrl,
  onExitWithoutRedirect,
}) => {
  const { t } = useTranslation('exercise');
  const navigate = useNavigate();
  // `isPaseStage` streams in from the websocket and can arrive/update *after* this
  // modal has mounted. Deriving directly from the prop (instead of seeding
  // useState once at mount) keeps the exercise list in sync with the latest
  // pause payload, so the movements render as soon as the backend sends them.
  const pauseData = isPaseStage;
  const [isGoing, setIsGoing] = useState(0);
  const [confirmAction, setConfirmAction] = useState(null);
  const [showExerciseDetails, setShowExerciseDetails] = useState(false);
  // Tracks the *playlist slot* being skipped, not the movex id — two
  // consecutive items (e.g. "Clamshell" then "Clamshell With Elastic Band")
  // can share the same exerciseId, so keying off exerciseId here would never
  // see the value change and the skip-tracking effect below would never fire.
  const skippedExerciseOrderRef = useRef(null);
  const currentItemRef = useRef(null);

  // `status === CURRENT` is the strongest signal, but it only flips once an
  // exercise actually starts — during its own INTRO every pauseStage item
  // still reports "not_started" (or "skipped", for one already passed), so
  // that lookup alone finds nothing yet. `currentExerciseOrder` (the
  // message's own `exercise_order`, not a field on the items) names which
  // exercise's `order` is active regardless, and is the correct fallback
  // before resorting to "just show the first entry in the list".
  const currentExercise =
    pauseData?.find(item => item.status === EXERCISE_STATUS.CURRENT) ??
    pauseData?.find(item => currentExerciseOrder !== null && item.order === currentExerciseOrder) ??
    null;
  const currentExerciseId = currentExercise?.exerciseId;
  const currentOrder = currentExercise?.order;

  useEffect(() => {
    if (pauseData) {
      setIsGoing(currentExerciseId);
    }
  }, [pauseData, currentExerciseId]);

  useEffect(() => {
    if (currentExerciseId == null) {
      return;
    }
    // Mobile portrait folds the whole modal into one scroll region (see the
    // block-layout override on the outer wrapper above) — the playlist sits
    // above the session actions there, so auto-scrolling it would keep
    // shoving "skip exercise" / "complete later" out of view. Desktop and
    // landscape keep the playlist as its own independently scrolling box,
    // where jumping to the current exercise is actually helpful.
    const isSingleScrollMobileLayout = window.matchMedia(
      '(max-width: 1023.98px) and (orientation: portrait)'
    ).matches;
    if (isSingleScrollMobileLayout) {
      return;
    }
    currentItemRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [currentExerciseId]);

  // "Show exercise details" only needs *an* exercise to show, not specifically
  // the one currently running — falls back to the first entry so it's usable
  // even before currentExercise resolves at all (e.g. the very first message).
  const detailsExercise = currentExercise ?? pauseData?.[0] ?? null;

  useEffect(() => {
    if (
      skippedExerciseOrderRef.current !== null &&
      (currentOrder == null || currentOrder !== skippedExerciseOrderRef.current)
    ) {
      skippedExerciseOrderRef.current = null;
      // Skip keeps the backend in IN_PERFORMANCE (only the current exercise
      // changes), so userTimer never re-anchors on its own — do it here, the
      // same moment restart does, once the backend confirms it actually moved
      // on. Deliberately NOT onRestartExercise: that one also suppresses the
      // "3, 2, 1" get-ready countdown (correct for an actual restart, which
      // drops back into the same exercise mid-stream) — a skip moves to a
      // genuinely new exercise, which must still get its own countdown.
      onExerciseSkipped?.();
      handleStopSend?.();
    }
  }, [currentOrder, handleStopSend, onExerciseSkipped]);

  const handelToggleMirror = () => {
    setToggleMirror(!toggleMirror);
  };

  const handleResume = () => {
    trackEvent('INPERFORMANCE_ACTION', {
      ...getInPerformanceTrackingProps(),
      action_type: IN_PERFORMANCE_ACTION_TYPES.RESUME_SESSION,
    });
    handleStopSend?.();
  };

  const handleShowExerciseDetails = () => {
    trackEvent('INPERFORMANCE_ACTION', {
      ...getInPerformanceTrackingProps(),
      action_type: IN_PERFORMANCE_ACTION_TYPES.SHOW_EXERCISE_DETAILS,
    });
    setShowExerciseDetails(true);
  };

  const totalCount = pauseData?.length || 0;
  const completedCount =
    pauseData?.filter(item => item.status === EXERCISE_STATUS.COMPLETED)?.length || 0;

  const handleConfirmSkip = () => {
    trackEvent('INPERFORMANCE_ACTION', {
      ...getInPerformanceTrackingProps(),
      action_type: IN_PERFORMANCE_ACTION_TYPES.SKIP_EXERCISE,
    });
    skippedExerciseOrderRef.current = currentOrder;
    sendJsonMessage({ pause: { action: SettingMode.skip, exerciseId: isGoing } });
    // Dismiss the confirm dialog right away. The modal itself closes as soon as
    // the backend advances off this exercise (see effect below) — the timeout is
    // only a fallback so the user is never stuck if that confirmation is slow.
    setConfirmAction(null);
  };

  const handleConfirmRestart = () => {
    trackEvent('INPERFORMANCE_ACTION', {
      ...getInPerformanceTrackingProps(),
      action_type: IN_PERFORMANCE_ACTION_TYPES.RESTART_EXERCISE,
    });
    sendJsonMessage({ pause: { action: SettingMode.restart } });
    onRestartExercise?.();
    setConfirmAction(null);
    handleClose?.();
  };

  const handleConfirmExit = () => {
    // Complete later hands off to leaveSession() right below, which does a
    // hard window.location.replace/navigate — the default async XHR track
    // call gets cancelled mid-flight by that unload, so this one needs
    // sendBeacon to actually reach Mixpanel.
    trackEvent(
      'INPERFORMANCE_ACTION',
      {
        ...getInPerformanceTrackingProps(),
        action_type: IN_PERFORMANCE_ACTION_TYPES.COMPLETE_LATER,
      },
      { transport: 'sendBeacon' }
    );
    // Tear down the session (stop camera, clear recording/local state), then
    // hand the user to the backend's exitUrl — normally the app that launched
    // this exercise.
    setConfirmAction(null);
    handleExit?.();

    // With no usable exitUrl there is no external app to return to, so the
    // exercise page routes the exit through its post-performance path instead
    // (end-of-session feedback popup, then the same redirect fallback the
    // completed-session flow uses). Only when the page hasn't opted into that
    // do we fall back to stepping one entry back in history.
    if (!normalizeRedirectUrl(exitUrl) && onExitWithoutRedirect) {
      onExitWithoutRedirect();
      return;
    }

    leaveSession({ url: exitUrl, navigate });
  };

  if (!openModal) {
    return null;
  }

  return (
    <>
      <div
        tw="fixed inset-0 z-[9999] bg-white flex flex-col overflow-hidden"
        // Portrait drops `flex` for plain block stacking. A flex container that
        // is *also* the `overflow-y-auto` scroll owner has a known iOS Safari
        // bug: its scrollHeight doesn't get recalculated when a flex child's
        // content grows after first layout — which is exactly what happens
        // here, since `isPaseStage`/pauseData streams in over the websocket
        // after this modal has already mounted. Every child in this chain is
        // already content-sized (`flex-none`) for portrait, so block layout
        // stacks them identically while sidestepping the bug.
        css={tw`max-lg:[@media(orientation:portrait)]:block max-lg:[@media(orientation:portrait)]:overflow-y-auto max-lg:[@media(orientation:portrait)]:pb-[80px]`}
      >
        <div tw="flex flex-col items-center justify-center px-[20px] lg:px-[80px] py-[32px] lg:py-[60px] shrink-0">
          <p tw="font-bold text-[32px] lg:text-[64px] tracking-[1.28px] text-[#475016] text-center">
            {t('settingsModal.title')}
          </p>
        </div>

        <div
          tw="flex flex-row min-h-0 flex-1 gap-[24px] lg:gap-[40px] px-[20px] lg:px-[80px] pb-[20px] lg:pb-0"
          css={tw`max-lg:[@media(orientation:portrait)]:flex-col max-lg:[@media(orientation:portrait)]:flex-none`}
        >
          {/* Session summary */}
          <div
            tw="order-1 relative flex flex-col gap-[24px] flex-1 min-h-0 min-w-0"
            css={tw`max-lg:[@media(orientation:portrait)]:order-2 max-lg:[@media(orientation:portrait)]:flex-none`}
          >
            <div tw="flex items-center flex-wrap justify-between px-[8px]">
              <p tw="font-semibold text-[20px] text-[#2E2E2E]">
                {t('settingsModal.sessionSummary')}
              </p>
              {totalCount > 0 && (
                <p tw="text-[18px] font-medium text-gray-text">
                  {t('settingsModal.exercisesCompleted', {
                    completed: completedCount,
                    total: totalCount,
                  })}
                </p>
              )}
            </div>
            <div
              tw="flex flex-col gap-[16px] flex-1 min-h-0 overflow-y-auto pe-3"
              css={tw`max-lg:[@media(orientation:portrait)]:flex-none max-lg:[@media(orientation:portrait)]:overflow-visible`}
            >
              {totalCount > 0 ? (
                pauseData.map(item => (
                  <div
                    key={item.order ?? item.id}
                    ref={item.order === currentOrder ? currentItemRef : null}
                  >
                    <ExerciseCard
                      title={item.exerciseName || t('settingsModal.exerciseFallbackTitle')}
                      setNumber={item.currentSet}
                      totalSets={item.exerciseConfig?.sets ?? item.totalSets}
                      status={item.status}
                      imageUrl={item.imageUrl}
                      isFlipped={item.isNeedFlipping}
                    />
                  </div>
                ))
              ) : (
                <div tw="flex items-center justify-center h-[75px] min-h-[75px] border border-dashed border-gray-300 rounded-[12px]">
                  <p tw="text-gray-500">{t('settingsModal.noExercisesAvailable')}</p>
                </div>
              )}
            </div>

            {/* Mobile/portrait inline resume banner (desktop uses the pinned footer instead) */}
            <div
              tw="hidden fixed bottom-[20px] left-[20px] right-[20px] items-center justify-between py-[6px] px-[12px] bg-[#F7FAEB] border border-[#718122] rounded-[12px] z-[10000]"
              css={tw`max-lg:[@media(orientation:portrait)]:flex`}
            >
              <p tw="text-[15px] font-medium text-[#2E2E2E]">{t('settingsModal.title')}</p>
              <button
                onClick={handleResume}
                tw="bg-secondary-color border border-[#8DA12B] px-[16px] py-[8px] rounded-[6px] text-[15px] font-medium text-[#2E2E2E]"
              >
                {t('settingsModal.resume')}
              </button>
            </div>
          </div>

          {/* Actions menu. `pe-6` rather than the summary column's `pe-3`: a
              narrower gutter left content sitting against the scrollbar. */}
          <div
            tw="order-2 flex flex-col md:flex-1 gap-[24px] lg:gap-[34px] min-h-0 min-w-0 overflow-y-auto pe-6"
            // md:flex-1 alone left this column unconstrained below the md
            // breakpoint — on a landscape phone narrower than that (still a row
            // layout, only portrait switches to flex-col below), it claimed
            // whatever width its content wanted and crushed the summary column
            // (flex-1 min-w-0, so it has nothing stopping it from being squeezed)
            // down to a sliver, wrapping "session summary" one word per line.
            // Portrait forces flex-none + overflow-visible so this column (and
            // the summary column below it) size to their own content instead of
            // splitting the row's flex space — the row's ancestor chain is all
            // flex-none in portrait now, so the single outer modal scrolls as
            // one region instead of each column scrolling/clipping internally.
            css={tw`max-lg:[@media(orientation:portrait)]:order-1 max-lg:[@media(orientation:portrait)]:flex-none max-lg:[@media(orientation:portrait)]:overflow-visible max-lg:[@media(orientation:landscape)]:flex-1`}
          >
            <div tw="flex flex-col gap-[12px] w-full">
              <p tw="font-semibold text-[20px] text-[#2E2E2E] w-full">
                {t('settingsModal.whatDoYouWantToDo')}
              </p>
              <TextWithIcon
                disabled={!detailsExercise}
                icon={<HiOutlineClipboardDocumentList size={20} />}
                text={t('settingsModal.showExerciseDetails')}
                handleClickSection={handleShowExerciseDetails}
              />
              <TextWithIcon
                icon={
                  <img
                    loading="lazy"
                    decoding="async"
                    alt=""
                    src={ReStartIcon}
                    tw="w-full h-full"
                  />
                }
                text={t('settingsModal.restartExercise')}
                handleClickSection={() => setConfirmAction('restart')}
              />
              <TextWithIcon
                icon={
                  <img loading="lazy" decoding="async" alt="" src={skipIcon} tw="w-full h-full" />
                }
                text={t('settingsModal.skipExercise')}
                handleClickSection={() => setConfirmAction('skip')}
              />
            </div>
            <div tw="border-t border-[#D9DFE4] pt-[4px] w-full">
              <TextWithIcon
                icon={
                  <img loading="lazy" decoding="async" alt="" src={ExitIcon} tw="w-full h-full" />
                }
                text={t('settingsModal.completeLater')}
                handleClickSection={() => setConfirmAction('exit')}
              />
            </div>

            {/* Tablet/landscape resume button — sits under the actions list instead of the
                page-wide pinned footer used on desktop */}
            <button
              onClick={handleResume}
              tw="hidden"
              css={tw`max-lg:[@media(orientation:landscape)]:flex justify-center gap-[8px] items-center bg-secondary-color border border-[#8DA12B] px-[16px] py-[12px] rounded-[6px] text-[15px] font-medium text-[#2E2E2E] w-full`}
            >
              {t('settingsModal.resumeExercise')}
              <IoArrowForward size={20} tw="rtl:scale-x-[-1]" />
            </button>
          </div>
        </div>

        {/* Desktop pinned footer */}
        <div
          tw="hidden bg-[#F8F9FA] border-t border-[#D9DFE4] justify-end items-center gap-[12px] p-[24px] shrink-0"
          css={tw`lg:flex`}
        >
          <button
            onClick={handleResume}
            tw="flex gap-[8px] items-center bg-secondary-color border border-[#8DA12B] px-[16px] py-[12px] rounded-[6px] text-[15px] font-medium text-[#2E2E2E]"
          >
            {t('settingsModal.resumeExercise')}
            <IoArrowForward size={20} tw="rtl:scale-x-[-1]" />
          </button>
        </div>
      </div>
      {confirmAction === 'restart' && (
        <ConfirmActionModal
          open={confirmAction === 'restart'}
          onCancel={() => setConfirmAction(null)}
          onConfirm={handleConfirmRestart}
          icon={ReStartIcon}
          iconBg="#EFF4D7"
          iconRingColor="#F7FAEB"
          title={t('settingsModal.restartExercise')}
          description={t('settingsModal.confirmRestartDescription')}
          confirmLabel={t('settingsModal.confirmRestartLabel')}
          confirmButtonStyle={tw`bg-secondary-color border-[#8DA12B] text-[#2E2E2E]`}
        />
      )}
      {confirmAction === 'skip' && (
        <ConfirmActionModal
          open={confirmAction === 'skip'}
          onCancel={() => setConfirmAction(null)}
          onConfirm={handleConfirmSkip}
          icon={skipIcon}
          iconBg="#EFF4D7"
          iconRingColor="#F7FAEB"
          title={t('settingsModal.skipExercise')}
          description={t('settingsModal.confirmSkipDescription')}
          confirmLabel={t('settingsModal.confirmSkipLabel')}
          confirmButtonStyle={tw`bg-secondary-color border-[#8DA12B] text-[#2E2E2E]`}
        />
      )}
      {confirmAction === 'exit' && (
        <ConfirmActionModal
          open={confirmAction === 'exit'}
          onCancel={() => setConfirmAction(null)}
          onConfirm={handleConfirmExit}
          icon={ExitIcon}
          iconBg="#EEF0F3"
          iconRingColor="#F8F9FA"
          title={t('settingsModal.completeLater')}
          description={t('settingsModal.confirmExitDescription')}
          confirmLabel={t('settingsModal.completeLater')}
          confirmButtonStyle={tw`bg-[#6D737B] border-[#6D737B] text-white`}
        />
      )}
      {showExerciseDetails && (
        <ExerciseDetailsModal
          open={showExerciseDetails}
          onClose={() => setShowExerciseDetails(false)}
          exercise={detailsExercise}
        />
      )}
    </>
  );
};

export default SettingModal;
